import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { fade } from '@smartera/animations';
import { chartAnimationIcons } from '@core/constant';
import { TableColumn, TableColumns } from '@smartera/core';
import { UtilityService } from '@core/services';
import { isObjectNotNull } from '@core/utils';
import {
	AggregationData,
	AggregationField,
	ElasticSearchQuery,
	FilterQuery,
	LabelMetaData,
} from '@feature/smart-reports-core/models';
import { UserWidget } from '@feature/smart-reports-core/models/user-widget';
import { ElasticSearchService, ESIndex, UserWidgetsService } from '@feature/smart-reports-core/services';
import { ReportUtils } from '@feature/smart-reports-core/utils';
import { BaseComponent } from '@modules/components';
import { catchError, finalize, lastValueFrom, Observable, of, takeUntil } from 'rxjs';
import {
	SmartyGptConversation,
	SmartyGptMessage,
	SmartyGptReport,
	SmartyGptResponse,
	SmartyGptResponseFilterDetails,
	SmartyGptResponseHeaderMetaData,
} from '../models/smarty-gpt';
import { SmartyGptService } from '../services/smarty-gpt.service';
import * as FileSaver from 'file-saver';

@Component({
	selector: 'smarty-gpt',
	templateUrl: './smarty-gpt.component.html',
	styleUrls: ['./smarty-gpt.component.scss'],
	animations: [fade],
	standalone: false,
})
export class SmartyGptComponent extends BaseComponent {
	@Input() index: ESIndex;

	readonly isObjectNotNull = isObjectNotNull;

	override states = {
		isFirstCreation: true,
		isTyping: false,
		isReadyReport: false,
	};

	messages: SmartyGptMessage[] = [];
	newMessage: string = '';
	selectedMessage: SmartyGptMessage;

	smartyGptReportErrorCount: number = 0;
	smartyGptReport: SmartyGptReport;
	smartyGptReportHistory: SmartyGptReport[] = [];

	@ViewChild('messageContainer') private messageContainer: ElementRef;
	@Output() createReportEvent: EventEmitter<SmartyGptResponse> = new EventEmitter();

	constructor(
		public smartyGptService: SmartyGptService,
		private elasticSearchService: ElasticSearchService,
		private userWidgetService: UserWidgetsService,
		private router: Router,
		utilServ: UtilityService
	) {
		super(null, utilServ);
	}

	init(): void {
		this.showLoader();
		this.startChartAnimation();
		this.resetUi();
		if (this.index) {
			this.smartyGptService.loadConversationByIndexId(this.index.id).subscribe(async (conversation) => {
				if (conversation) {
					for (const item of conversation) {
						if (item.role === 'user') {
							this.messages.push({ user: this.user.name, text: <string>item.content });
						} else {
							const response: SmartyGptResponse = <SmartyGptResponse>item.content;
							response.esQuery = this.prepareElasticSearchBody(response.esQuery);
							this.messages.push({ user: 'SMARTY-GPT', text: response.description });
							if (response.isQuery) {
								await this.initSmartyGptTable(response);
							}
						}
					}
				}
				//NOTE: Konuşmalar yüklendikten sonra en aşağıya gitmesi için settimeout içerisine aldık.
				setTimeout(() => {
					if (this.messages.length > 0) {
						this.states.isFirstCreation = false;
					}
					this.states.isReadyReport = true;
					this.hideloader();
					this.scrollToBottom();
				}, 1);
			});
		}
	}

	sendMessage(): void {
		if (this.newMessage.trim()) {
			this.messages.push({ user: this.user.name, text: this.newMessage });
			this.askSmartyGpt(this.newMessage);
			this.newMessage = '';
			setTimeout(() => this.scrollToBottom(), 100);
		}
	}

	askSmartyGpt(message: string): void {
		this.states = {
			isTyping: true,
			isReadyReport: false,
			isFirstCreation: false,
		};
		this.smartyGptService
			.queryWithAssistantByIndexId({
				question: message,
				indexId: this.index.id,
			})
			.pipe(
				takeUntil(this.destroy$),
				catchError(() => this.askSmartyGptHandleError()),
				finalize(() => this.askSmartyGptHandleFinish())
			)
			.subscribe((response) => {
				if (response) {
					const message: SmartyGptResponse = <SmartyGptResponse>response[1].content;
					message.esQuery = this.prepareElasticSearchBody(message.esQuery);
					if (message.isQuery) {
						this.initSmartyGptTable(message).then(() =>
							this.completeCreationSmartyGptTable(response, this.smartyGptReport.isSuccess)
						);
					} else {
						this.completeCreationSmartyGptTable(response, true);
					}
				}
			});
	}

	completeCreationSmartyGptTable(response: SmartyGptConversation[], isSuccess: boolean): void {
		if (isSuccess) {
			const message: SmartyGptResponse = <SmartyGptResponse>response[1].content;
			this.smartyGptService.saveLogToHistoryByIndexId(this.index.id, response).subscribe(() => null);
			this.smartyGptReportErrorCount = 0;
			this.messages.push({ user: 'SMARTY-GPT', text: message.description });
		} else {
			this.messages.push({
				user: 'SMARTY-GPT',
				text: this.t('pages.smarty-gpt.create-report-error-message'),
			});
		}
		this.states.isTyping = false;
		this.states.isReadyReport = true;
	}

	askSmartyGptHandleError(): Observable<null> {
		this.messages.push({ user: 'SMARTY-GPT', text: this.t('pages.smarty-gpt.gpt-response-error-message') });
		return of(null);
	}

	askSmartyGptHandleFinish(): void {
		this.states.isReadyReport = true;
		this.states.isTyping = false;
		this.scrollToBottom();
	}

	private prepareElasticSearchBody(body: ElasticSearchQuery): ElasticSearchQuery {
		if (!body.query) {
			body.query = { bool: {} };
		}
		return body;
	}

	initSmartyGptTable(smartyGptResponse: SmartyGptResponse): Promise<void> {
		return new Promise((resolve) => {
			this.elasticSearchService
				.getSmartyGptData(this.index.name, smartyGptResponse.esQuery)
				.pipe(
					takeUntil(this.destroy$),
					catchError((error: HttpErrorResponse) => this.initSmartyGptDataHandleError(error))
				)
				.subscribe((data) => {
					this.setUserMessageResponseIndex();
					const labelMetaData = this.getLabelMetaData(smartyGptResponse.headerMetaData);
					this.smartyGptReport = {
						data: data ?? [],
						columns: this.prepareColumns(data, labelMetaData),
						description: smartyGptResponse.description,
						metaData: labelMetaData,
						query: smartyGptResponse.esQuery,
						filterDetails: smartyGptResponse.filterDetails,
						isSuccess: data ? true : false,
					};
					this.smartyGptReportHistory.push(this.smartyGptReport);
					resolve();
				});
		});
	}

	initSmartyGptDataHandleError(error: HttpErrorResponse): Observable<[]> {
		console.log('error: ', error);
		if (error?.status === 400 && this.smartyGptReportErrorCount < 1) {
			this.smartyGptReportErrorCount += 1;
			this.askSmartyGpt(this.t('pages.smarty-gpt.rebuild-query') + '\n' + JSON.stringify(error?.error));
		} else {
			this.smartyGptReportErrorCount = 0;
			return of(null);
		}
	}

	private setUserMessageResponseIndex(): void {
		const gptResponseIndex = this.smartyGptReportHistory.length;
		const lastUserMessageIndex = this.findLastUserMessageIndex();
		this.selectedMessage = this.messages[lastUserMessageIndex];
		this.selectedMessage.responseIndex = gptResponseIndex;
	}

	private findLastUserMessageIndex(): number {
		for (let i = this.messages.length - 1; i >= 0; i--) {
			if (this.messages[i].user === this.user.name) {
				return i;
			}
		}
		return -1;
	}

	private getLabelMetaData(headerMetaData: SmartyGptResponseHeaderMetaData): LabelMetaData {
		const meta = {};
		headerMetaData.headers.forEach((metaData) => {
			meta[metaData.path] = {
				...metaData.properties,
				path: metaData.path,
				label: metaData.label,
			};
		});
		return meta;
	}

	private prepareColumns(data: object[] | null, metaData: LabelMetaData): TableColumn<unknown>[] {
		if (data && data.length > 0) {
			const columns = Object.keys(data[0]);
			return ReportUtils.prepareDataGridTableColumns(columns, metaData);
		} else {
			return [];
		}
	}

	async clearConversationHistory(): Promise<void> {
		await this.confirm({
			message: 'pages.smarty-gpt.clear-conversation-confirm-message',
		});
		this.showLoader();
		this.smartyGptService.clearConversationByIndexId(this.index.id).subscribe(this.updateHandler(() => this.resetUi()));
	}

	async save(): Promise<void> {
		this.showLoader();
		const { columns, metaData, query, filterDetails } = this.smartyGptReport;
		const reportName = 'SmartyGPT Report ' + new Date().getTime();
		const fields = await lastValueFrom(this.elasticSearchService.getColumnMetadata(this.index.name));

		const filterValues = this.prepareFilterValues(fields, filterDetails);
		let filterQuery: FilterQuery;
		Object.keys(query.query.bool).forEach((mustOrFilter: string) => {
			filterQuery = query.query.bool[mustOrFilter];
		});

		const reportType = query.aggs ? 'pivottable' : 'datagrid';

		const userWidget = {
			scheduledDelivery: false,
			reportReceivers: [],
			cronExpression: null,
			explanation: reportName,
			name: reportName,
			indexId: this.index.id,
			useAsIndicator: false,
			applicationIds: [],
			canOtherUsersAccess: false,
			body: {
				data: JSON.stringify({
					fields: fields,
					filter: {
						selectedFilters: filterDetails.selectedFilters ?? [],
						filterValues: filterValues,
						query: filterQuery ?? [],
					},
					reportOption: this.prepareReportOptions(reportType, columns, metaData),
					title: reportName,
					explanation: reportName,
					applicationIds: [],
					scheduledDelivery: false,
					useAsIndicator: false,
					canOtherUsersAccess: false,
					source: this.index.name,
					cronData: {},
					type: reportType,
				}),
			},
		};
		this.userWidgetService.createObject(userWidget).subscribe(
			this.createHandler((res: UserWidget) => {
				this.router.navigateByUrl(`/page/smart-report/${reportType}/${this.index.name}/${res.id}`);
				this.createReportEvent.emit();
			})
		);
	}

	private prepareReportOptions(
		reportType: 'datagrid' | 'pivottable',
		columns: TableColumns<unknown>,
		metaData: LabelMetaData
	): object {
		if (reportType === 'datagrid') {
			return {
				columns: columns.map((column) => {
					return { name: metaData[column.field].field_name };
				}),
			};
		} else {
			const selectedMethods = this.extractAggregations(this.smartyGptReport.query.aggs);
			const rows = [];
			columns.forEach((column) => {
				const isFieldAggregationMethod = selectedMethods.some(
					(selectedMethod) => selectedMethod.field === metaData[column.field].field_name
				);
				if (!isFieldAggregationMethod) {
					rows.push({
						name: metaData[column.field].field_name,
						type: metaData[column.field].type,
						dateFormat: null,
						orderBy: null,
						order: null,
						size: 100,
					});
				}
			});
			return {
				rows: rows,
				columns: [],
				selectedMethods: selectedMethods,
				cumulativeSum: false,
				sortOrder: null,
				sortField: null,
			};
		}
	}

	extractAggregations(aggData: AggregationData): AggregationField[] {
		const result: AggregationField[] = [];
		const aggregationTypes = ['avg', 'sum', 'max', 'min', 'value_count', 'cardinality'];

		function processAggregations(aggs: AggregationData): void {
			for (const [key, value] of Object.entries(aggs)) {
				for (const aggType of aggregationTypes) {
					if (Object.prototype.hasOwnProperty.call(value, aggType)) {
						result.push({
							method: aggType,
							field: value[aggType].field,
						});
					}
				}

				if (Object.prototype.hasOwnProperty.call(value, 'aggs')) {
					processAggregations(value.aggs);
				}
			}
		}

		processAggregations(aggData);
		return result;
	}

	private prepareFilterValues(fields: LabelMetaData, filterDetails: SmartyGptResponseFilterDetails): object {
		const filterValues = {};
		for (const field of Object.keys(fields)) {
			filterValues[field] = {
				field: field,
				operator: 'match',
				firstSearchPart: null,
				lastSearchPart: null,
			};
		}

		// NOTE: text tipindeki değelerin array içerisine yazılması gerekmektedir.
		// Bu düzenleme chatGPT tarafında da yapılabilir.
		if (filterDetails?.filterValues) {
			Object.keys(filterDetails?.filterValues).forEach((field) => {
				if (fields[field].type === 'text') {
					filterValues[field] = {
						...filterDetails.filterValues[field],
						firstSearchPart: [filterDetails.filterValues[field].firstSearchPart],
					};
				} else {
					filterValues[field] = filterDetails.filterValues[field];
				}
			});
		}
		return filterValues;
	}

	override resetUi(): void {
		this.messages = [];
		this.selectedMessage = null;
		this.smartyGptReport = null;
		this.smartyGptReportHistory = [];
		this.states = {
			isFirstCreation: true,
			isTyping: false,
			isReadyReport: false,
		};
	}

	selectMessage(message: SmartyGptMessage, index: number): void {
		if (isObjectNotNull(message?.responseIndex) && message.user === this.user.name) {
			this.states.isReadyReport = false;
			this.selectedMessage = this.messages[index];
			this.smartyGptReport = this.smartyGptReportHistory[message.responseIndex];
			setTimeout(() => {
				this.states.isReadyReport = true;
			}, 500);
		}
	}

	private scrollToBottom(): void {
		try {
			this.messageContainer.nativeElement.scrollTop = this.messageContainer.nativeElement.scrollHeight;
		} catch (err) {
			console.log(err);
		}
	}

	public chartAnimationIcon: string;

	startChartAnimation(): void {
		let index = 0;
		setInterval(() => {
			this.chartAnimationIcon = chartAnimationIcons[index].icon;
			index = (index + 1) % chartAnimationIcons.length;
		}, 1500);
	}

	async excelExport(): Promise<void> {
		this.showLoader();
		const excelData = this.smartyGptReport.data.map((item: unknown) => {
			const rowData = {};
			this.smartyGptReport.columns
				.map((column) => column.field)
				.forEach((column: string) => {
					rowData[this.smartyGptReport.metaData[column]?.label ?? column] = item[column];
				});
			return rowData;
		});
		import('xlsx').then((xlsx) => {
			const worksheet = xlsx.utils.json_to_sheet(excelData);
			const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
			const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });

			const reportName = 'SmartyGPT Report ' + new Date().getTime();
			this.saveAsExcelFile(excelBuffer, reportName);
		});
		this.hideloader();
	}

	saveAsExcelFile(buffer: BlobPart, fileName: string): void {
		const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
		const EXCEL_EXTENSION = '.xlsx';
		const data: Blob = new Blob([buffer], {
			type: EXCEL_TYPE,
		});
		FileSaver.saveAs(data, fileName + EXCEL_EXTENSION);
	}
}
